import React, { useState, useEffect, useCallback } from 'react';

const DRIVER_COLORS = {
  'Fraud Enablement': 'bg-red-700',
  'Data Breach': 'bg-orange-600',
  'Operational Disruption': 'bg-amber-600',
};

const CONF_STYLES = {
  CONFIRMED: 'bg-red-700 text-white',
  PROBABLE: 'bg-amber-600 text-white',
  POTENTIAL: 'bg-slate-500 text-white',
};

const BOOL_FIELDS = [
  ['accesses_pii', '¿El agente accede a datos personales de clientes?'],
  ['accesses_financial_data', '¿Accede a datos financieros o de facturación?'],
  ['can_execute_transactions', '¿Puede ejecutar transacciones (pagos, altas, bajas)?'],
  ['can_authorize_or_approve', '¿Puede autorizar o aprobar operaciones?'],
  ['has_connected_tools', '¿Tiene herramientas o APIs internas conectadas?'],
  ['serves_end_customers', '¿Atiende directamente a clientes finales?'],
  ['influences_business_decisions', '¿Sus respuestas influyen en decisiones del negocio?'],
];

const AUTONOMY = [
  ['suggest_only', 'Solo sugiere o responde; no ejecuta acciones'],
  ['act_with_approval', 'Ejecuta acciones con aprobación humana previa'],
  ['act_autonomously', 'Ejecuta acciones de forma autónoma'],
];

const emptyForm = {
  bot_url: '', bot_label: '', connected_tools_detail: '', autonomy_level: 'suggest_only',
  ...Object.fromEntries(BOOL_FIELDS.map(([k]) => [k, false])),
};

export const AgentLossMapping = ({ botUrl }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ ...emptyForm, bot_url: botUrl || '' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/loss-mapping/company', { headers });
      setData(await r.json());
    } catch (e) {
      setError('No se pudo cargar el Loss Mapping');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body = { ...form, bot_url: form.bot_url || botUrl || 'default' };
      const r = await fetch('/api/loss-mapping/agent-profile',
        { method: 'POST', headers, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).detail || 'Error al guardar');
      setEditing(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmMapping = async (fp) => {
    const note = window.prompt('Nota de validación del analista (qué se verificó y con quién):');
    if (!note) return;
    const r = await fetch('/api/loss-mapping/confirm',
      { method: 'POST', headers, body: JSON.stringify({ finding_fingerprint: fp, note }) });
    if (!r.ok) setError((await r.json()).detail);
    await load();
  };

  if (!data) return null;

  // ---------- Gating: sin declaración no hay Loss Mapping ----------
  if (!data.available || editing) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6" data-testid="loss-mapping-gate">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">💼 Cyber Loss Mapping</h3>
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          {data.reason || 'Actualizá la declaración de capacidades del agente.'}
        </p>
        <div className="bg-slate-50 border-l-4 border-slate-400 p-3 mb-5 text-xs text-slate-600">
          El tipo de pérdida asegurable no depende de la severidad técnica del hallazgo, sino de{' '}
          <strong>qué puede hacer económicamente un atacante si manipula este agente</strong>. Por eso
          hace falta que el cliente declare sus capacidades.
        </div>

        <div className="space-y-3 mb-5">
          {BOOL_FIELDS.map(([key, label]) => (
            <label key={key} className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                data-testid={`agent-profile-${key}`}
                checked={!!form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                className="mt-1 h-4 w-4 accent-indigo-600"
              />
              <span className="text-sm text-slate-700 group-hover:text-slate-900">{label}</span>
            </label>
          ))}
        </div>

        {form.has_connected_tools && (
          <input
            type="text"
            data-testid="agent-profile-tools-detail"
            placeholder="¿Qué herramientas? (ej. CRM, pasarela de pagos, ERP)"
            value={form.connected_tools_detail}
            onChange={(e) => setForm({ ...form, connected_tools_detail: e.target.value })}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm mb-5"
          />
        )}

        <div className="mb-5">
          <div className="text-sm font-semibold text-slate-700 mb-2">Nivel de autonomía</div>
          {AUTONOMY.map(([val, label]) => (
            <label key={val} className="flex items-center gap-3 cursor-pointer mb-1">
              <input
                type="radio"
                name="autonomy"
                data-testid={`agent-autonomy-${val}`}
                checked={form.autonomy_level === val}
                onChange={() => setForm({ ...form, autonomy_level: val })}
                className="h-4 w-4 accent-indigo-600"
              />
              <span className="text-sm text-slate-700">{label}</span>
            </label>
          ))}
        </div>

        {error && <div className="text-sm text-red-600 mb-3" data-testid="loss-mapping-error">{error}</div>}

        <button
          onClick={save}
          disabled={saving}
          data-testid="save-agent-profile-btn"
          className="px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors disabled:opacity-50"
        >
          {saving ? 'Guardando…' : 'Guardar declaración y calcular Loss Mapping'}
        </button>
      </div>
    );
  }

  // ---------- Loss Mapping disponible ----------
  const amps = [];
  (data.mappings || []).forEach((m) => (m.severity_amplifiers || []).forEach((a) => {
    if (!amps.find((x) => x.factor === a.factor)) amps.push(a);
  }));

  return (
    <div className="bg-white rounded-lg shadow-lg p-6" data-testid="loss-mapping-panel">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="text-2xl font-bold text-gray-900">💼 Cyber Loss Mapping</h3>
        <button
          onClick={() => setEditing(true)}
          data-testid="edit-agent-profile-btn"
          className="text-xs text-indigo-600 hover:text-indigo-800 underline shrink-0"
        >
          Editar declaración del agente
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4">{data.basis}</p>

      <div className="flex flex-wrap gap-4 mb-5">
        <div className="bg-slate-900 text-white rounded-lg px-4 py-3" data-testid="lm-primary-driver">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Primary loss driver</div>
          <div className="text-lg font-bold">{data.primary_loss_driver}</div>
          {data.secondary_loss_drivers?.length > 0 && (
            <div className="text-[11px] text-slate-300 mt-0.5">
              Secundarios: {data.secondary_loss_drivers.join(' · ')}
            </div>
          )}
        </div>
        <div className="bg-slate-100 rounded-lg px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Loss driver confidence</div>
          <div className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-bold ${CONF_STYLES[data.loss_driver_confidence]}`}>
            {data.loss_driver_confidence}
          </div>
        </div>
        <div className="bg-slate-100 rounded-lg px-4 py-3 max-w-md">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Capacidades declaradas</div>
          <div className="text-xs text-slate-700 mt-1">
            {(data.agent_capabilities_declared || []).join(' · ') || 'ninguna'}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Autonomía: {data.autonomy_label}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b">
              <th className="py-2 pr-3">Hallazgo</th>
              <th className="py-2 pr-3">Capacidad de ataque</th>
              <th className="py-2 pr-3">Capacidad de negocio expuesta</th>
              <th className="py-2 pr-3">Loss driver</th>
              <th className="py-2 pr-3">Confianza</th>
              <th className="py-2">Relev.</th>
            </tr>
          </thead>
          <tbody>
            {(data.mappings || []).map((m, i) => (
              <tr key={i} className="border-b last:border-0 align-top" data-testid={`lm-row-${i}`}>
                <td className="py-3 pr-3">
                  <div className="font-medium text-gray-900">{m.finding_title}</div>
                  <div className="text-[10px] text-gray-500">
                    Severidad técnica: {m.technical_severity} · Evidencia: {m.evidence_level}
                  </div>
                </td>
                <td className="py-3 pr-3 text-xs text-gray-700">{m.attack_capability}</td>
                <td className="py-3 pr-3 text-xs text-gray-700">{m.business_capability_exposed}</td>
                <td className="py-3 pr-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold text-white ${DRIVER_COLORS[m.primary_loss_driver] || 'bg-slate-400'}`}>
                    {m.primary_loss_driver}
                  </span>
                  {m.secondary_loss_drivers?.length > 0 && (
                    <div className="text-[10px] text-gray-500 mt-1">+ {m.secondary_loss_drivers.join(', ')}</div>
                  )}
                </td>
                <td className="py-3 pr-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${CONF_STYLES[m.loss_driver_confidence]}`}>
                    {m.loss_driver_confidence}
                  </span>
                  {m.evidence_level === 'PROVEN' && m.loss_driver_confidence !== 'CONFIRMED' && (
                    <button
                      onClick={() => confirmMapping(m.finding_fingerprint)}
                      data-testid={`lm-confirm-${i}`}
                      className="block mt-1 text-[10px] text-indigo-600 hover:text-indigo-800 underline"
                    >
                      Validar como CONFIRMED
                    </button>
                  )}
                  {m.analyst_confirmation && (
                    <div className="text-[10px] text-gray-500 mt-1">
                      por {m.analyst_confirmation.confirmed_by}
                    </div>
                  )}
                </td>
                <td className="py-3 text-xs text-gray-700">{m.underwriting_relevance}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {amps.length > 0 && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="lm-amplifiers">
          <div className="text-sm font-bold text-amber-900 mb-1">Amplificadores de severidad</div>
          <div className="text-xs text-amber-800 mb-2">
            Modifican la severidad esperada de la pérdida, no su tipo. No cambian el loss driver.
          </div>
          <ul className="list-disc list-inside text-xs text-amber-900 space-y-1">
            {amps.map((a, i) => <li key={i}><strong>{a.factor}</strong>: {a.effect}</li>)}
          </ul>
        </div>
      )}

      {error && <div className="text-sm text-red-600 mt-3" data-testid="loss-mapping-error">{error}</div>}

      <div className="text-[11px] text-gray-500 mt-4 leading-relaxed">
        <strong>CONFIRMED</strong> requiere validación de un analista respaldada por evidencia técnica
        demostrada · <strong>PROBABLE</strong> = capacidad de ataque demostrada sobre una capacidad de
        negocio declarada · <strong>POTENTIAL</strong> = el vector existe sin impacto económico demostrado.
      </div>
    </div>
  );
};

export default AgentLossMapping;
